import React, {useCallback, useEffect, useState} from 'react';
import {Box, CircularProgress, FormControlLabel, Switch, Typography,} from '@mui/material';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {parseError} from '../../services/http.service';
import {ConfigurationEntry, listConfig, setConfig} from '../../services/config.service';
import {useTranslation} from 'react-i18next';
import {Header, StyledContainer} from "./Configuration.styled";

const BIELIK_GUARD_KEY = 'prompt.moderation.enabled';

type ConfigType = 'boolean' | 'string';

interface ConfigDefinition {
    labelKey: string;
    descriptionKey?: string;
    type: ConfigType;
}

const CONFIG_DEFINITIONS: Record<string, ConfigDefinition> = {
    [BIELIK_GUARD_KEY]: {
        labelKey: 'configuration.bielikGuard',
        descriptionKey: 'configuration.bielikGuardDescription',
        type: 'boolean',
    },
};

const Configuration: React.FC = () => {
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const [loading, setLoading] = useState(false);
    const [updatingKey, setUpdatingKey] = useState<string | null>(null);
    const [entries, setEntries] = useState<ConfigurationEntry[]>([]);

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listConfig();
            setEntries(data);
        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.loadError'), 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnackbar, t]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const handleBooleanToggle = async (
        name: string,
        checked: boolean
    ) => {
        setUpdatingKey(name);

        const previousEntries = entries;
        const newValue = checked ? 'true' : 'false';

        setEntries(prev =>
            prev.map(e =>
                e.propertyName === name ? {...e, propertyValue: newValue} : e
            )
        );

        try {
            await setConfig(name, newValue);
            showSnackbar(t('configuration.updateSuccess'), 'success');
        } catch (e) {
            console.error(e);
            showSnackbar(parseError(e) || t('configuration.updateError'), 'error');
            // revert na błąd
            setEntries(previousEntries);
        } finally {
            setUpdatingKey(null);
        }
    };

    const visibleEntries = entries.filter(
        (entry) => CONFIG_DEFINITIONS[entry.propertyName]
    );

    return (
        <StyledContainer role="region" aria-labelledby="configuration-title">
            <Header>
                <Typography id="configuration-title" variant="h4" component="h2"> {t('sidebar.configuration')}</Typography>
            </Header>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}} aria-live="polite" aria-atomic={true} aria-busy={loading} role="group" aria-label={t('configuration.settingsGroupLabel') as string}>

                {loading && (
                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2, position: 'relative'}}>
                        <CircularProgress size={24} sx={{mr: 1}} aria-hidden={true}/>
                        <Typography variant="body2">
                            {t('configuration.loading')}
                        </Typography>
                        <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('configuration.loading')}
                        </Box>
                    </Box>
                )}

                {visibleEntries.map((entry) => {
                    const definition = CONFIG_DEFINITIONS[entry.propertyName];

                    if (!definition) {
                        return null;
                    }

                    const isUpdating = updatingKey === entry.propertyName;

                    if (definition.type === 'boolean') {
                        const checked = entry.propertyValue === 'true';

                        return (
                            <Box
                                key={entry.propertyName}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2,
                                }}
                                aria-busy={isUpdating}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minHeight: 48,
                                    }}
                                >
                                    {isUpdating && (
                                        <>
                                            <CircularProgress size={20} sx={{mr: 1}} aria-hidden={true}/>
                                            <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                                                {t('configuration.loading')}
                                            </Box>
                                        </>
                                    )}
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={checked}
                                                onChange={(_, newChecked) =>
                                                    handleBooleanToggle(
                                                        entry.propertyName,
                                                        newChecked
                                                    )
                                                }
                                                disabled={loading || isUpdating}
                                                inputProps={{
                                                    'aria-describedby': definition.descriptionKey ? `${entry.propertyName}-desc` : undefined,
                                                }}
                                            />
                                        }
                                        label={t(definition.labelKey)}
                                    />
                                </Box>

                                {definition.descriptionKey && (
                                    <Typography
                                        id={`${entry.propertyName}-desc`}
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ml: 6}}
                                    >
                                        {t(definition.descriptionKey)}
                                    </Typography>
                                )}
                            </Box>
                        );
                    }
                    return null;
                })}
            </Box>
        </StyledContainer>
    );
};

export default Configuration;
